import axios from "axios";
import { tokenStore } from "./tokenStore";
import { refresh } from "@/services/authService";
import {
  WORKSPACE_HEADER,
  TEAM_VIEW_HEADER,
  shouldTagEmployeeWorkspace,
  shouldTagTeamView,
} from "./workspace";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  timeout: 0,
});

function isAuthEndpoint(url = "") {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/logout") ||
    url.includes("/auth/verify-otp") ||
    url.includes("/auth/generate-otp")
  );
}

let isRefreshing = false;
let pendingQueue = [];
let refreshPromise = null;
let refreshFailed = false;
let authExpiredDispatched = false;

export function resetRefreshFailure() {
  refreshFailed = false;
  authExpiredDispatched = false;
}

function processQueue(error, token = null) {
  pendingQueue.forEach((promise) => {
    if (error) promise.reject(error);
    else promise.resolve(token);
  });
  pendingQueue = [];
}

function getBearerToken(authHeader = "") {
  if (typeof authHeader !== "string") return null;
  return authHeader.startsWith("Bearer ") ? authHeader.slice(7) : null;
}

function dispatchAuthExpiredOnce() {
  if (authExpiredDispatched) return;
  authExpiredDispatched = true;
  window.dispatchEvent(new Event("auth-expired"));
}

/* ===============================
   REQUEST INTERCEPTOR
   =============================== */
axiosInstance.interceptors.request.use(
  async (config) => {
    if (!isAuthEndpoint(config.url)) {
      // If refresh is in-flight, wait — but never throw from here
      if (refreshPromise) {
        await refreshPromise.catch(() => { });
      }

      const token = tokenStore.get();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      else delete config.headers.Authorization;

      if (shouldTagEmployeeWorkspace(config)) {
        config.headers[WORKSPACE_HEADER] = "employee";
      } else {
        delete config.headers[WORKSPACE_HEADER];
      }

      if (shouldTagTeamView(config)) {
        config.headers[TEAM_VIEW_HEADER] = "team";
      } else {
        delete config.headers[TEAM_VIEW_HEADER];
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ===============================
   RESPONSE INTERCEPTOR
   =============================== */
axiosInstance.interceptors.response.use(
  (response) => {
    const data = response?.data;
    // ⚠️ SOFT SUBSCRIPTION WARNING (200 OK)
    if (data?.subscriptionWarning) {
      window.dispatchEvent(
        new CustomEvent("subscription-warning", {
          detail: { ...data.subscriptionWarning, ...data.subscription },
        })
      );
    }

    if (data?.subscription?.status === "active" || data?.subscription?.status === "trial") {
      window.dispatchEvent(
        new CustomEvent("subscription-active", {
          detail: data.subscription
        })
    );
    }

    return response;
  },
  async (error) => {
    // 🌐 NETWORK / OFFLINE DETECTION
    // No `response` means the request never reached the server (device offline,
    // DNS failure, timeout, server unreachable). Signal the network layer so the
    // global OfflineFallback can take over instead of surfacing an error toast.
    if (!error?.response) {
      window.dispatchEvent(new Event("network-error"));
    }

    const data = error?.response?.data;

    // 🔐 SUBSCRIPTION BLOCK (GLOBAL)
    if (data?.code === "SUBSCRIPTION_INACTIVE") {
      window.dispatchEvent(
        new CustomEvent("subscription-blocked", {
          detail: {
            status: data.subscription_status,
            redirect: data.redirect,
          },
        })
      );

      return Promise.reject(error);
    }

    if (data?.code === "SUBSCRIPTION_REQUIRED") {
      window.dispatchEvent(
        new CustomEvent("subscription-required", {
          detail: {
            redirect: data.redirect,
          },
        })
      );

      return Promise.reject(error);
    }

    const originalRequest = error?.config || {};

    // If not 401 OR already retried → fail
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      isAuthEndpoint(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    if (refreshFailed) {
      // session is dead; don't spam refresh anymore
      dispatchAuthExpiredOnce();
      return Promise.reject(error);
    }

    // Mark request as retried
    originalRequest._retry = true;

    // If another request already refreshed and this request failed with an older token,
    // replay immediately with latest token instead of triggering another refresh.
    const latestToken = tokenStore.get();
    const requestToken = getBearerToken(originalRequest?.headers?.Authorization);
    if (latestToken && requestToken !== latestToken) {
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${latestToken}`;
      return axiosInstance(originalRequest);
    }

    // If refresh already in progress, wait
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    refreshPromise = (async () => {
      const res = await refresh();
      const newToken = res.access_token;

      tokenStore.set(newToken);
      refreshFailed = false;
      authExpiredDispatched = false;
      processQueue(null, newToken);

      return newToken;
    })();

    try {
      const newToken = await refreshPromise;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      refreshFailed = true;
      processQueue(refreshError, null);
      tokenStore.clear();
      dispatchAuthExpiredOnce();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  }
);

export default axiosInstance;
